import { fmt$ } from "@/lib/fundready/report-content"

import type { FundReadyReport } from "@boundless/bl-api-client/public/models/FundReadyReport"

type FundingRangeBarProps = {
  report: FundReadyReport
  askAmount: number
}

export function FundingRangeBar({ report, askAmount }: FundingRangeBarProps) {
  const rangeMax = Math.max(report.totalCapacity, askAmount) * 1.15 || 1
  const pct = (v: number) => Math.min(100, (v / rangeMax) * 100)

  return (
    <>
      <svg
        className="my-[26px] mb-1.5 h-[54px] w-full overflow-visible"
        preserveAspectRatio="none"
      >
        <defs>
          <pattern
            id="asset-unlock-stripes"
            width="10"
            height="10"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width="10" height="10" className="fill-fr-brand-tint" />
            <rect width="5" height="10" className="fill-fr-brand" />
          </pattern>
        </defs>
        <rect
          x="0"
          y="24"
          width="100%"
          height="8"
          rx="4"
          className="fill-fr-border"
        />
        <rect
          x={`${pct(report.rangeLow)}%`}
          y="24"
          width={`${pct(report.rangeHigh) - pct(report.rangeLow)}%`}
          height="8"
          rx="4"
          className="fill-fr-brand"
        />
        {report.assetUnlock > 0 && (
          <rect
            x={`${pct(report.rangeHigh)}%`}
            y="24"
            width={`${pct(report.totalCapacity) - pct(report.rangeHigh)}%`}
            height="8"
            rx="4"
            fill="url(#asset-unlock-stripes)"
            opacity={0.8}
          />
        )}
        <rect
          x={`${pct(askAmount)}%`}
          y="10"
          width="2"
          height="36"
          className="fill-fr-ink"
        />
        <text
          x={`${pct(askAmount)}%`}
          y="4"
          textAnchor="middle"
          fontSize="11.5"
          fontWeight="600"
          className="fill-fr-ink"
        >
          ask {fmt$(askAmount)}
        </text>
      </svg>
      <div className="text-fr-muted flex justify-between text-[11.5px]">
        <span>$0</span>
        <span>{fmt$(rangeMax)}</span>
      </div>
      <div className="text-fr-muted mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11.5px]">
        <span className="flex items-center gap-1.5">
          <span className="bg-fr-brand inline-block h-2.5 w-4 rounded-sm" />
          Unsecured capacity
        </span>
        {report.assetUnlock > 0 && (
          <span className="flex items-center gap-1.5">
            {/* Reuses the "asset-unlock-stripes" pattern defined in the
                <defs> of the bar svg above, so the swatch stays in sync with
                it rather than drifting out of a second copy. */}
            <svg className="h-2.5 w-4 rounded-sm" aria-hidden="true">
              <rect
                width="100%"
                height="100%"
                fill="url(#asset-unlock-stripes)"
                opacity={0.8}
              />
            </svg>
            Secured capacity - additional, with assets pledged
          </span>
        )}
      </div>
    </>
  )
}
